import { RefreshCw } from 'lucide-react';
import { cx } from './cx';
import { Button } from './Button';
import { Icon } from './Icon';
import { typography } from './Text';
import { useT } from '../../modules/i18n';

export interface RefreshButtonProps {
  onRefresh: () => void;
  /** When provided, shows a "Last sync HH:MM" mono caption beside the button. */
  lastSync?: Date;
  tooltip?: string;
  label?: string;
  isDisabled?: boolean;
}

function formatLastSync(date?: Date): string {
  if (!date) return '—';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Toolbar refresh control: a secondary button with the refresh glyph and an
 * optional mono "Last sync" caption. Every page header that refetches data
 * uses this, customizing only `onRefresh`, `lastSync` and `tooltip`.
 */
export const RefreshButton = ({
  onRefresh,
  lastSync,
  tooltip,
  label,
  isDisabled,
}: RefreshButtonProps) => {
  const t = useT();
  const resolvedLabel = label ?? t('ui.refreshButton.label');
  return (
    <span className="inline-flex flex-wrap items-center gap-2.5">
      {lastSync !== undefined && (
        <span
          className={cx(
            typography.caption,
            typography.mono,
            'whitespace-nowrap text-fg-tertiary',
          )}
        >
          {t('ui.refreshButton.lastSync', { time: formatLastSync(lastSync) })}
        </span>
      )}
      <Button
        variant="secondary"
        size="sm"
        isDisabled={isDisabled}
        aria-label={tooltip ?? resolvedLabel}
        onPress={onRefresh}
        iconLeading={<Icon as={RefreshCw} size="sm" />}
      >
        {resolvedLabel}
      </Button>
    </span>
  );
};
